import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Html } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useSceneStore } from "@/state/useSceneStore";
import { HOTSPOTS, type HotspotKey } from "@/scene/fx/hotspotRegistry";
import { runHotspotAction } from "@/lib/actions";
import type { ObjectName } from "@/types";

const HOTSPOT_OBJECT_NAMES: ObjectName[] = [
  "book",
  "monitor",
  "pinBoard",
  "switch",
  "cube",
  "keyboard",
  "plant",
  "fan",
  "phone",
  "cpu",
  "mug",
];

const NAME_TO_KEY: Partial<Record<string, HotspotKey>> = {
  Book: "book",
  Stand: "monitor",
  SwitchBoard: "switch",
  Rubik_Cube: "cube",
  Keyboard: "keyboard",
  Plant_Pot: "plant",
  CPU: "cpu",
  Mobile: "phone",
  Coffe: "mug",
};

const ANCESTOR_DEPTH = 4;
const DWELL_MS = 400;

const findHotspot = (obj: THREE.Object3D): HotspotKey | null => {
  let cur: THREE.Object3D | null = obj;
  let depth = 0;
  while (cur && depth < ANCESTOR_DEPTH) {
    const k = NAME_TO_KEY[cur.name];
    if (k) return k;
    cur = cur.parent;
    depth += 1;
  }
  return null;
};

export const Hotspots = (): JSX.Element => {
  const { camera, mouse, raycaster, gl } = useThree();
  const setHovered = useSceneStore((s) => s.setHoveredObject);

  const [dwellKey, setDwellKey] = useState<HotspotKey | null>(null);
  const dwellTimer = useRef<number | null>(null);
  const pendingKey = useRef<HotspotKey | null>(null);
  const lastHovered = useRef<HotspotKey | null>(null);
  const labelPos = useRef<THREE.Vector3>(new THREE.Vector3());
  const [, forceLabel] = useState(0);

  const targets = useMemo<THREE.Object3D[]>(() => [], []);

  useFrame(() => {
    const objs = useSceneStore.getState().objects;
    targets.length = 0;
    for (const name of HOTSPOT_OBJECT_NAMES) {
      const o = objs[name];
      if (o) targets.push(o);
    }
    if (targets.length === 0) return;

    raycaster.setFromCamera(mouse, camera);
    const hits = raycaster.intersectObjects(targets, true);

    let foundKey: HotspotKey | null = null;
    for (const h of hits) {
      const k = findHotspot(h.object);
      if (k) {
        foundKey = k;
        labelPos.current.copy(h.point);
        break;
      }
    }

    pendingKey.current = foundKey;
    if (foundKey === lastHovered.current) return;
    lastHovered.current = foundKey;

    if (dwellTimer.current !== null) {
      window.clearTimeout(dwellTimer.current);
      dwellTimer.current = null;
    }

    if (foundKey) {
      const captured = foundKey;
      dwellTimer.current = window.setTimeout(() => {
        if (pendingKey.current === captured) {
          setHovered(captured);
          setDwellKey(captured);
          forceLabel((x) => x + 1);
        }
      }, DWELL_MS);
    } else {
      setHovered(null);
      setDwellKey(null);
    }
  });

  useEffect(() => {
    const canvas = gl.domElement;
    const onClick = (): void => {
      const live = pendingKey.current;
      const confirmed = dwellKey;
      if (!live || live !== confirmed) return;
      const meta = HOTSPOTS[live];
      if (meta.action) runHotspotAction(meta.action);
    };
    canvas.addEventListener("click", onClick);
    return () => canvas.removeEventListener("click", onClick);
  }, [gl, dwellKey]);

  useEffect(() => {
    return () => {
      if (dwellTimer.current !== null) window.clearTimeout(dwellTimer.current);
    };
  }, []);

  if (!dwellKey) return <></>;
  const meta = HOTSPOTS[dwellKey];
  return (
    <Html
      position={[labelPos.current.x, labelPos.current.y, labelPos.current.z]}
      zIndexRange={[10, 0]}
    >
      <div className="pointer-events-none -translate-x-1/2 -translate-y-1/2 rounded-sm border border-phosphor bg-bg1/80 px-2 py-1 text-xs uppercase tracking-wider font-mono text-phosphor">
        [ {meta.label}
        {meta.shortcut ? ` · ${meta.shortcut.toUpperCase()}` : ""} ]
      </div>
    </Html>
  );
};
